import {createContext, useContext, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { API_ERROR_CODE } from '@etherworks/shared-types';

import { normalizeThemePreference, ThemePreference } from '../utils/theme';
import { getApiErrorMessage } from '../utils/errorMessages';

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

// In production, when frontend is hosted on a different origin (e.g. Cloudflare Pages),
// all API requests must target the backend server's absolute URL.
// In development, requests use relative paths so the Vite dev proxy handles routing
// (same-origin), which allows httpOnly cookies to be sent correctly.
if (import.meta.env.VITE_API_URL && import.meta.env.PROD) {
  axios.defaults.baseURL = import.meta.env.VITE_API_URL;
}

// Function to clear all cookies comprehensively
const clearCookies = () => {
  console.log('Current cookies before clearing:', document.cookie);
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const cookieStr = cookie.trim();
    if (!cookieStr) continue;

    const eqPos = cookieStr.indexOf('=');
    const name = eqPos > -1 ? cookieStr.substring(0, eqPos).trim() : cookieStr.trim();

    console.log(`Attempting to clear cookie: ${name}`);

    // Try multiple combinations to ensure the cookie is cleared
    const clearAttempts = [
      // Basic clearing
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; Secure;`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; HttpOnly;`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax;`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict;`,

      // With current domain
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}; Secure;`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}; SameSite=Lax;`,

      // With root domain (remove www if present)
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname.replace(/^www\./, '')};`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname.replace(/^www\./, '')}; Secure;`,

      // Subdomain attempts
      ...(window.location.hostname.includes('.') ? [
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname};`,
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}; Secure;`,
      ] : []),

      // Additional paths
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/api;`,
      `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/api; domain=${window.location.hostname};`,
    ];

    // Execute all clear attempts
    clearAttempts.forEach(attempt => {
      try {
        document.cookie = attempt;
      } catch (e) {
        console.warn(`Failed to clear cookie with attempt: ${attempt}`, e);
      }
    });
  }

  // Also try to clear common auth-related cookie names directly
  const commonAuthCookies = ['token', 'auth_token', 'session_token', 'access_token', 'refresh_token', 'jwt', 'session'];
  commonAuthCookies.forEach(cookieName => {
    const clearAttempts = [
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`,
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`,
      `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname};`,
    ];

    clearAttempts.forEach(attempt => {
      try {
        document.cookie = attempt;
      } catch (e) {
        console.warn(`Failed to clear common cookie ${cookieName}: ${attempt}`, e);
      }
    });
  });

  console.log('Cookies after clearing attempt:', document.cookie);
  console.log('Cookie clearing completed');
};

const SESSION_AUTH_ERROR_CODES = new Set<number>([
  API_ERROR_CODE.AUTHENTICATION_REQUIRED,
  API_ERROR_CODE.TOKEN_ERROR,
  API_ERROR_CODE.NO_SUCH_USER,
]);

function isSessionAuthError(error: any): boolean {
  if (error?.response?.status !== 401) return false;
  const code = Number(error.response.data?.code);
  return SESSION_AUTH_ERROR_CODES.has(code);
}

type ProfileUpdates = {
  email?: string;
  preferences?: {
    theme?: ThemePreference;
    defaultModel?: string;
    language?: string;
  };
};

interface User {
  id: string;
  email: string;
  avatar?: string;
  role: 'user' | 'admin';
  tokens?: number;
  balance?: number;
  preferences: {
    theme: ThemePreference;
    defaultModel: string;
    language?: string;
  };
  subscription: {
    status: string;
    plan?: 'monthly' | 'quarterly' | 'yearly';
    tier?: 'free' | 'plus' | 'max' | 'ultra';
    activatedAt?: string;
    expiresAt?: string;
  };
  usageWindow?: {
    count: number;
    windowStart: string;
  };
  emailVerification: {
    isVerified: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (
    email: string,
    password: string,
    captchaId?: string,
    captchaAnswer?: string,
    rememberMe?: boolean,
    twoFactorCode?: string,
    challengeToken?: string,
  ) => Promise<{ needsEmailVerification?: boolean; needsTwoFactor?: boolean; user?: User | null; challengeToken?: string }>;
  register: (
    email: string,
    password: string,
    captchaId: string,
    captchaAnswer: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: ProfileUpdates) => Promise<void>;
  refreshUser: () => Promise<void>;
  sendVerificationCode: (email: string) => Promise<any>;
  verifyEmail: (email: string, code: string) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const mapUserResponse = (userData: any): User => {
  const preferences = userData?.preferences || {};
  return {
    ...userData,
    preferences: {
      ...preferences,
      theme: normalizeThemePreference(preferences.theme),
    },
  } as User;
};

const extractUserFromPayload = (payload: any): User | null =>
  payload?.user ? mapUserResponse(payload.user) : null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('auth');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const authCheckInProgressRef = useRef(false);

  const clearAuthState = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('authChecked');
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (authCheckInProgressRef.current) {
        return;
      }

      authCheckInProgressRef.current = true;

      try {
        const response = await axios.get('/api/auth/me');
        setUser(extractUserFromPayload(response.data));
      } catch (error: any) {
        clearAuthState();
      } finally {
        setLoading(false);
        authCheckInProgressRef.current = false;
      }
    };

    // Clear auth state on 401 responses
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (isSessionAuthError(error)) {
          clearAuthState();
        }
        return Promise.reject(error);
      }
    );

    void checkAuth();

    const revalidateSession = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      void checkAuth();
    };

    // Clear session storage flags when page unloads
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('authChecked');
      sessionStorage.removeItem('modelsLoaded');
      sessionStorage.removeItem('conversationsLoaded');
    };

    window.addEventListener('focus', revalidateSession);
    document.addEventListener('visibilitychange', revalidateSession);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      axios.interceptors.response.eject(resInterceptor);
      window.removeEventListener('focus', revalidateSession);
      document.removeEventListener('visibilitychange', revalidateSession);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const login = async (
    email: string,
    password: string,
    captchaId?: string,
    captchaAnswer?: string,
    rememberMe: boolean = false,
    twoFactorCode?: string,
    challengeToken?: string,
  ) => {
    try {
      const payload = challengeToken
        ? {
            challengeToken,
            twoFactorCode,
          }
        : {
            email,
            password,
            captchaId,
            captchaAnswer,
            rememberMe,
            ...(twoFactorCode ? { twoFactorCode } : {}),
          };
      const response = await axios.post('/api/auth/login', payload);
      if (response.data?.requires2FA) {
        return {
          needsEmailVerification: false,
          needsTwoFactor: true,
          challengeToken: response.data.challengeToken,
        };
      }
      const userData = extractUserFromPayload(response.data);
      setUser(userData);
      toast.success(t('welcome_back'));
      return { needsEmailVerification: false, needsTwoFactor: false, user: userData };
    } catch (error: any) {
      if (error.response?.status === 403 && error.response?.data?.needsEmailVerification) {
        return { needsEmailVerification: true };
      }

      // Let the calling component handle the error display
      throw error;
    }
  };


  const register = async (
    email: string,
    password: string,
    captchaId: string,
    captchaAnswer: string
  ) => {
    await axios.post('/api/auth/register', {
      email,
      password,
      captchaId,
      captchaAnswer,
    });
    toast.success(t('account_created'));

    // Refresh user status after successful registration
    try {
      await refreshUser();
    } catch (refreshError) {
      // Silently fail if refresh fails - user will still need to verify email and login
      console.log('Could not refresh user status after registration:', refreshError);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
      clearAuthState();
      toast.success(t('logged_out'));
      window.location.href = '/';
    } catch (error: any) {
      clearAuthState();
      toast.error(t('logout_failed'));
      throw error;
    }
  };

  const sendVerificationCode = async (email: string) => {
    const response = await axios.post('/api/auth/send-verification', { email });
    return response.data;
  };

  const verifyEmail = async (email: string, code: string) => {
    const response = await axios.post('/api/auth/verify-email', { email, code });
    const userData = extractUserFromPayload(response.data);
    if (userData) {
      setUser(userData);
    }
    return response.data;
  };

  const updateProfile = async (updates: ProfileUpdates) => {
    try {
      const response = await axios.patch('/api/auth/profile', updates);
      setUser(extractUserFromPayload(response.data));
      toast.success(t('profile_updated'));
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, t, 'update_failed'));
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(extractUserFromPayload(response.data));
    } catch (error: any) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    sendVerificationCode,
    verifyEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
